import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function PinSetup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Setup form
  const [setupForm, setSetupForm] = useState({ pin: "", confirmPin: "" });
  
  // Change form
  const [changeForm, setChangeForm] = useState({ currentPin: "", newPin: "", confirmNewPin: "" });
  
  // Active tab
  const [activeTab, setActiveTab] = useState("status");

  // Backup codes
  const [backupCodes, setBackupCodes] = useState([]);
  const [verifyPinForBackup, setVerifyPinForBackup] = useState("");

  // Auth logs
  const [authLogs, setAuthLogs] = useState([]);

  const token = localStorage.getItem("token");
  const api = axios.create({
    baseURL: "/api",
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    loadStatus();
    loadAuthLogs();
  }, []);

  const loadStatus = async () => {
    try {
      const res = await api.get("/TwoFactor/status");
      setStatus(res.data);
    } catch (err) {
      console.error("Failed to load 2FA status", err);
    }
  };

  const loadAuthLogs = async () => {
    try {
      const res = await api.get("/TwoFactor/logs?count=10");
      setAuthLogs(res.data);
    } catch (err) {
      console.error("Failed to load auth logs", err);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (setupForm.pin !== setupForm.confirmPin) {
      setError("PINs do not match");
      return;
    }

    if (setupForm.pin.length < 4 || setupForm.pin.length > 6) {
      setError("PIN must be 4-6 digits");
      return;
    }

    if (!/^\d+$/.test(setupForm.pin)) {
      setError("PIN must contain only digits");
      return;
    }

    setLoading(true);
    try {
      await api.post("/TwoFactor/setup", {
        pin: setupForm.pin,
        confirmPin: setupForm.confirmPin
      });
      setSuccess("Transaction PIN set up successfully!");
      setSetupForm({ pin: "", confirmPin: "" });
      loadStatus();
      setActiveTab("status");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to set up PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (changeForm.newPin !== changeForm.confirmNewPin) {
      setError("New PINs do not match");
      return;
    }

    if (changeForm.newPin.length < 4 || changeForm.newPin.length > 6) {
      setError("PIN must be 4-6 digits");
      return;
    }

    setLoading(true);
    try {
      await api.post("/TwoFactor/change", {
        currentPin: changeForm.currentPin,
        newPin: changeForm.newPin,
        confirmNewPin: changeForm.confirmNewPin
      });
      setSuccess("Transaction PIN changed successfully!");
      setChangeForm({ currentPin: "", newPin: "", confirmNewPin: "" });
      loadStatus();
      setActiveTab("status");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to change PIN");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    const pin = prompt("Enter your current PIN to disable 2FA:");
    if (!pin) return;

    setLoading(true);
    setError("");
    try {
      await api.post("/TwoFactor/disable", { pin });
      setSuccess("2FA has been disabled");
      loadStatus();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to disable 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBackupCodes = async () => {
    if (!verifyPinForBackup) {
      setError("Please enter your PIN to generate backup codes");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await api.post("/TwoFactor/backup-codes/generate", {
        pin: verifyPinForBackup
      });
      setBackupCodes(res.data.codes);
      setVerifyPinForBackup("");
      setSuccess("Backup codes generated! Save them securely.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate backup codes");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => new Date(date).toLocaleString();

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🔐 Transaction PIN Setup</h1>
        <button onClick={() => navigate("/dashboard")} style={styles.btnOutline}>
          ← Dashboard
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        {["status", "setup", "change", "backup", "logs"].map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setError(""); setSuccess(""); }}
            style={{
              ...styles.tab,
              ...(activeTab === tab ? styles.tabActive : {})
            }}
          >
            {tab === "status" && "📊 Status"}
            {tab === "setup" && "🆕 Setup PIN"}
            {tab === "change" && "🔄 Change PIN"}
            {tab === "backup" && "💾 Backup Codes"}
            {tab === "logs" && "📜 Activity Log"}
          </button>
        ))}
      </div>

      {/* Status Tab */}
      {activeTab === "status" && status && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>2FA Status</h3>
          
          <div style={styles.statusGrid}>
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Status</span>
              <span style={{
                ...styles.statusBadge,
                backgroundColor: status.isEnabled ? "#28a745" : "#dc3545"
              }}>
                {status.isEnabled ? "✓ Enabled" : "✗ Not Enabled"}
              </span>
            </div>
            
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Auth Type</span>
              <span style={styles.statusValue}>{status.authType}</span>
            </div>
            
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Required for Transactions</span>
              <span style={styles.statusValue}>
                {status.requiredForTransactions ? "Yes" : "No"}
              </span>
            </div>
            
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Account Status</span>
              <span style={{
                ...styles.statusBadge,
                backgroundColor: status.isLocked ? "#dc3545" : "#28a745"
              }}>
                {status.isLocked ? "🔒 Locked" : "🔓 Active"}
              </span>
            </div>
            
            {status.isLocked && status.lockedUntil && (
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Locked Until</span>
                <span style={styles.statusValue}>{formatDate(status.lockedUntil)}</span>
              </div>
            )}
            
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Failed Attempts</span>
              <span style={{
                ...styles.statusValue,
                color: status.failedAttempts > 0 ? "#dc3545" : "#28a745"
              }}>
                {status.failedAttempts} / 5
              </span>
            </div>
            
            {status.lastVerifiedAt && (
              <div style={styles.statusItem}>
                <span style={styles.statusLabel}>Last Verified</span>
                <span style={styles.statusValue}>{formatDate(status.lastVerifiedAt)}</span>
              </div>
            )}
            
            <div style={styles.statusItem}>
              <span style={styles.statusLabel}>Backup Codes</span>
              <span style={styles.statusValue}>
                {status.hasBackupCodes ? "✓ Generated" : "✗ Not Generated"}
              </span>
            </div>
          </div>

          {status.isEnabled && (
            <div style={styles.actions}>
              <button onClick={handleDisable} style={styles.btnDanger}>
                Disable 2FA
              </button>
            </div>
          )}

          {!status.isEnabled && (
            <div style={styles.infoBox}>
              <p>
                <strong>🔐 Secure Your Transactions</strong><br />
                Set up a transaction PIN to protect your account. You'll need to enter this PIN to authorize deposits, withdrawals, and transfers.
              </p>
              <button 
                onClick={() => setActiveTab("setup")} 
                style={styles.btnPrimary}
              >
                Set Up PIN Now
              </button>
            </div>
          )}
        </div>
      )}

      {/* Setup Tab */}
      {activeTab === "setup" && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Set Up Transaction PIN</h3>
          
          <form onSubmit={handleSetup}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Enter PIN (4-6 digits)</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={setupForm.pin}
                onChange={(e) => setSetupForm({ ...setupForm, pin: e.target.value.replace(/\D/g, "") })}
                style={styles.input}
                placeholder="Enter 4-6 digit PIN"
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm PIN</label>
              <input
                type="password"
                inputMode="numeric"
                maxLength={6}
                value={setupForm.confirmPin}
                onChange={(e) => setSetupForm({ ...setupForm, confirmPin: e.target.value.replace(/\D/g, "") })}
                style={styles.input}
                placeholder="Confirm your PIN"
                required
              />
            </div>

            <div style={styles.infoBox}>
              <p>
                <strong>Tips for a secure PIN:</strong>
                <ul style={{ margin: "10px 0", paddingLeft: "20px" }}>
                  <li>Don't use easily guessable numbers (1234, 0000)</li>
                  <li>Don't use your birth date</li>
                  <li>Don't share your PIN with anyone</li>
                </ul>
              </p>
            </div>
            
            <button 
              type="submit" 
              disabled={loading}
              style={{
                ...styles.btnPrimary,
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? "Setting up..." : "Set Up PIN"}
            </button>
          </form>
        </div>
      )}

      {/* Change Tab */}
      {activeTab === "change" && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Change Transaction PIN</h3>
          
          {!status?.isEnabled ? (
            <div style={styles.infoBox}>
              <p>You need to set up a PIN first before you can change it.</p>
              <button 
                onClick={() => setActiveTab("setup")} 
                style={styles.btnPrimary}
              >
                Set Up PIN
              </button>
            </div>
          ) : (
            <form onSubmit={handleChange}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Current PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={changeForm.currentPin}
                  onChange={(e) => setChangeForm({ ...changeForm, currentPin: e.target.value.replace(/\D/g, "") })}
                  style={styles.input}
                  placeholder="Enter current PIN"
                  required
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>New PIN (4-6 digits)</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={changeForm.newPin}
                  onChange={(e) => setChangeForm({ ...changeForm, newPin: e.target.value.replace(/\D/g, "") })}
                  style={styles.input}
                  placeholder="Enter new PIN"
                  required
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm New PIN</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={changeForm.confirmNewPin}
                  onChange={(e) => setChangeForm({ ...changeForm, confirmNewPin: e.target.value.replace(/\D/g, "") })}
                  style={styles.input}
                  placeholder="Confirm new PIN"
                  required
                />
              </div>
              
              <button 
                type="submit" 
                disabled={loading}
                style={{
                  ...styles.btnPrimary,
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? "Changing..." : "Change PIN"}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Backup Codes Tab */}
      {activeTab === "backup" && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Backup Codes</h3>
          
          {!status?.isEnabled ? (
            <div style={styles.infoBox}>
              <p>You need to set up a PIN first before generating backup codes.</p>
            </div>
          ) : backupCodes.length > 0 ? (
            <>
              <div style={styles.warningBox}>
                <p>
                  <strong>⚠️ Save these codes!</strong><br />
                  These codes can only be shown once. Store them securely. Each code can only be used once.
                </p>
              </div>
              
              <div style={styles.codesGrid}>
                {backupCodes.map((code, i) => (
                  <div key={i} style={styles.codeItem}>
                    {i + 1}. <code style={styles.code}>{code}</code>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={() => {
                  const text = backupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n");
                  navigator.clipboard.writeText(text);
                  alert("Backup codes copied to clipboard!");
                }}
                style={styles.btnSecondary}
              >
                📋 Copy All Codes
              </button>
              
              <button 
                onClick={() => setBackupCodes([])}
                style={{ ...styles.btnOutline, marginLeft: "10px" }}
              >
                Done
              </button>
            </>
          ) : (
            <>
              <p style={styles.description}>
                Backup codes can be used to access your account if you forget your PIN. 
                Each code can only be used once.
              </p>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Enter your PIN to generate backup codes</label>
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={6}
                  value={verifyPinForBackup}
                  onChange={(e) => setVerifyPinForBackup(e.target.value.replace(/\D/g, ""))}
                  style={styles.input}
                  placeholder="Enter your PIN"
                />
              </div>
              
              <button 
                onClick={handleGenerateBackupCodes}
                disabled={loading || !verifyPinForBackup}
                style={{
                  ...styles.btnPrimary,
                  opacity: loading || !verifyPinForBackup ? 0.6 : 1
                }}
              >
                {loading ? "Generating..." : "Generate Backup Codes"}
              </button>
            </>
          )}
        </div>
      )}

      {/* Activity Logs Tab */}
      {activeTab === "logs" && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>Recent Activity</h3>
          
          {authLogs.length === 0 ? (
            <p style={styles.description}>No activity recorded yet.</p>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Action</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Transaction</th>
                </tr>
              </thead>
              <tbody>
                {authLogs.map((log) => (
                  <tr key={log.id}>
                    <td style={styles.td}>{formatDate(log.createdAt)}</td>
                    <td style={styles.td}>{log.action}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        backgroundColor: log.success ? "#28a745" : "#dc3545",
                        padding: "3px 8px",
                        fontSize: "11px"
                      }}>
                        {log.success ? "Success" : "Failed"}
                      </span>
                      {log.failureReason && (
                        <div style={styles.failureReason}>{log.failureReason}</div>
                      )}
                    </td>
                    <td style={styles.td}>
                      {log.transactionType && (
                        <>
                          {log.transactionType}
                          {log.transactionAmount && (
                            <span style={styles.amount}>
                              ₦{log.transactionAmount.toLocaleString()}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          <button 
            onClick={loadAuthLogs}
            style={styles.btnSecondary}
          >
            🔄 Refresh
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
    minHeight: "100vh",
    backgroundColor: "#f5f5f5"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px"
  },
  title: {
    margin: 0,
    fontSize: "24px"
  },
  tabs: {
    display: "flex",
    gap: "5px",
    marginBottom: "20px",
    flexWrap: "wrap"
  },
  tab: {
    padding: "10px 16px",
    border: "none",
    backgroundColor: "#e9ecef",
    borderRadius: "8px 8px 0 0",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "500"
  },
  tabActive: {
    backgroundColor: "#007bff",
    color: "white"
  },
  card: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "25px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
  },
  cardTitle: {
    margin: "0 0 20px 0",
    fontSize: "18px",
    borderBottom: "1px solid #eee",
    paddingBottom: "15px"
  },
  statusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
    marginBottom: "20px"
  },
  statusItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px"
  },
  statusLabel: {
    fontSize: "12px",
    color: "#666",
    fontWeight: "500"
  },
  statusValue: {
    fontSize: "14px",
    fontWeight: "600"
  },
  statusBadge: {
    display: "inline-block",
    padding: "5px 12px",
    borderRadius: "20px",
    color: "white",
    fontSize: "12px",
    fontWeight: "500"
  },
  formGroup: {
    marginBottom: "20px"
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "500",
    color: "#333"
  },
  input: {
    width: "100%",
    padding: "12px 15px",
    fontSize: "16px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    boxSizing: "border-box"
  },
  infoBox: {
    backgroundColor: "#e3f2fd",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px"
  },
  warningBox: {
    backgroundColor: "#fff3cd",
    padding: "15px",
    borderRadius: "8px",
    marginBottom: "20px",
    borderLeft: "4px solid #ffc107"
  },
  codesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
    marginBottom: "20px"
  },
  codeItem: {
    padding: "10px",
    backgroundColor: "#f8f9fa",
    borderRadius: "6px",
    fontFamily: "monospace"
  },
  code: {
    backgroundColor: "#e9ecef",
    padding: "2px 6px",
    borderRadius: "4px"
  },
  description: {
    color: "#666",
    marginBottom: "20px"
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "20px"
  },
  th: {
    textAlign: "left",
    padding: "12px 10px",
    borderBottom: "2px solid #dee2e6",
    backgroundColor: "#f8f9fa",
    fontSize: "12px",
    fontWeight: "600"
  },
  td: {
    padding: "10px",
    borderBottom: "1px solid #eee",
    fontSize: "13px"
  },
  failureReason: {
    fontSize: "11px",
    color: "#dc3545",
    marginTop: "3px"
  },
  amount: {
    display: "block",
    fontSize: "11px",
    color: "#28a745",
    fontWeight: "600"
  },
  actions: {
    display: "flex",
    gap: "10px",
    marginTop: "20px"
  },
  btnPrimary: {
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer"
  },
  btnSecondary: {
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer"
  },
  btnOutline: {
    backgroundColor: "white",
    color: "#007bff",
    border: "1px solid #007bff",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer"
  },
  btnDanger: {
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    fontSize: "14px",
    cursor: "pointer"
  },
  error: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px"
  },
  success: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "12px 15px",
    borderRadius: "8px",
    marginBottom: "15px"
  }
};

